import React, { useState } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  MessageCircle,
  Instagram,
  Facebook,
  Youtube,
  Send,
  CheckCircle2,
} from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';
import useSeo from '../hooks/useSeo';
import { sendContactMessage } from '../services/api';

export default function Contact() {
  const { config } = useSiteConfig();

  // 1️⃣ ALL hooks must be called unconditionally at the top
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  // 2️⃣ useSeo must also be called unconditionally
  //    Use optional chaining (?.) to avoid crashes when config is null
  useSeo({
    title: 'Contact Us — Studio, Custom Orders & Bulk Enquiries',
    description: `Get in touch with ${config?.brand || 'us'}. Call ${config?.phone || ''}, WhatsApp us, or drop a message for custom orders and bulk enquiries.`,
    canonical: '/contact',
    type: 'website',
  });

  // 3️⃣ Now the conditional guard – AFTER all hooks
  if (!config) {
    return (
      <main className="bg-[#f6f2ea] min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <p className="text-neutral-600">Loading contact info…</p>
      </main>
    );
  }

  // 4️⃣ Handlers (they can safely use config now)
  const change = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await sendContactMessage(form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSent(false);
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setError('');
  };

  // 5️⃣ JSX – no changes needed, config is guaranteed to be defined
  return (
    <main className="bg-[#f6f2ea] min-h-[calc(100vh-4rem)]">
      {/* Hero */}
      <section className="relative bg-[#1e3a2b] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="font-serif text-4xl sm:text-5xl">Get in touch</h1>
          <p className="mt-3 text-neutral-200 max-w-xl mx-auto">
            Have a question about a piece, a custom order, or a bulk enquiry?
            We&#39;d love to hear from you.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        {/* Left: contact details */}
        <aside className="lg:col-span-1 space-y-4">
          {/* Phone */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1e3a2b] text-white grid place-items-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-widest text-neutral-500">
                  CALL US
                </div>
                <a
                  href={`tel:${config.phone}`}
                  className="font-semibold text-neutral-900 hover:text-[#b8935a]"
                >
                  {config.phone}
                </a>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Mon-Sat &bull; 10:00 to 19:00 IST
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#25D366] text-white grid place-items-center shrink-0">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-widest text-neutral-500">
                  WHATSAPP
                </div>
                <a
                  href={`https://wa.me/${config.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-neutral-900 hover:text-[#b8935a]"
                >
                  Chat with us
                </a>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Typical reply in 15 minutes
                </div>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#b8935a] text-white grid place-items-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-widest text-neutral-500">
                  EMAIL
                </div>
                <a
                  href={`mailto:${config.email}`}
                  className="font-semibold text-neutral-900 hover:text-[#b8935a] break-all"
                >
                  {config.email}
                </a>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-neutral-800 text-white grid place-items-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-semibold tracking-widest text-neutral-500">
                  STUDIO
                </div>
                <p className="font-medium text-neutral-900 leading-relaxed">
                  {config.address}
                </p>
              </div>
            </div>
          </div>

          {/* Socials */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="text-xs font-semibold tracking-widest text-neutral-500 mb-3">
              FOLLOW US
            </div>
            <div className="flex gap-3">
              {config.socials?.facebook && (
                <a
                  href={config.socials.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-full border border-neutral-300 grid place-items-center text-neutral-700 hover:bg-[#1e3a2b] hover:text-white hover:border-[#1e3a2b] transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {config.socials?.instagram && (
                <a
                  href={config.socials.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-full border border-neutral-300 grid place-items-center text-neutral-700 hover:bg-[#1e3a2b] hover:text-white hover:border-[#1e3a2b] transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {config.socials?.youtube && (
                <a
                  href={config.socials.youtube}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-full border border-neutral-300 grid place-items-center text-neutral-700 hover:bg-[#1e3a2b] hover:text-white hover:border-[#1e3a2b] transition-colors"
                >
                  <Youtube className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </aside>

        {/* Right: form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-6 sm:p-8">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-[#1e3a2b]/10 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-[#1e3a2b]" />
                </div>
                <h2 className="mt-4 font-serif text-2xl text-neutral-900">
                  Thanks, {form.name.split(' ')[0] || 'friend'}!
                </h2>
                <p className="mt-2 text-neutral-600">
                  Your message is on its way. We usually reply within a few hours.
                </p>
                <button
                  onClick={resetForm}
                  className="mt-6 border border-[#1e3a2b] text-[#1e3a2b] hover:bg-[#1e3a2b] hover:text-white px-6 py-2.5 rounded-md font-semibold transition-colors"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h2 className="font-serif text-2xl text-neutral-900">
                  Send us a message
                </h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-neutral-600">
                      Full name *
                    </label>
                    <input
                      required
                      value={form.name}
                      onChange={change('name')}
                      className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={change('email')}
                      className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">
                      Mobile
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={change('phone')}
                      className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">
                      Subject
                    </label>
                    <input
                      value={form.subject}
                      onChange={change('subject')}
                      placeholder="Custom order, bulk enquiry, feedback…"
                      className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-600">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={change('message')}
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md border border-red-200">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1e3a2b] hover:bg-[#2a5040] text-white font-bold px-6 py-3 rounded-md tracking-wide transition-colors inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>Sending…</>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Map */}
          {config.mapEmbedUrl && (
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <iframe
                title="Studio location"
                src={config.mapEmbedUrl}
                className="w-full h-72 sm:h-96"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}